// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertCircle,
  Wrench,
  RefreshCw,
  Plus,
  Trash2,
  PackagePlus,
  CheckCircle2,
  Search,
  MapPin,
  Calendar,
  Layers,
  ArrowRight,
  ShieldCheck,
  Building2,
  Eye,
  Gauge,
  Image as ImageIcon,
  Scissors,
  Printer,
  Clock,
  Sparkles,
  Info,
  Truck,
  Undo2,
  QrCode,
  Send,
  Flame,
} from "lucide-react";
import ImageLightbox from "@/components/shared/ImageLightbox";
import { getFullUrl } from "@/lib/uploadFile";
import { formatNumber, formatLocation } from "@/lib/utils";
import { EntityLink } from "@/components/shared/EntityLink";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import PrintDialog from "@/components/shared/PrintDialog";
import MoldLabelPrintTemplate from "@/pages/tech/molds/print/MoldLabelPrintTemplate";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/ui/numeric-input";
import { useVendorLookup, useMoldLookup } from "@/hooks/useFKLookup";
import { evaluatePlateDurability, toVnDateString, isOutsourcedPlate, isDieAssignableToLine } from "@apv-erp/status";
import { ComponentGroupRow } from "../components/ComponentGroupRow";
import { isComponentTreeMode, orderComponentTree } from "../utils/component-tree";

export interface ToolingStandardInfo {
  prefix: string;
  name: string;
  techTerm: string;
  badgeClass: string;
}

export const TOOLING_STANDARDS: Record<string, ToolingStandardInfo> = {
  DIE_CUT: {
    prefix: "KB",
    name: "Khuôn bế",
    techTerm: "Die Cutting - DIE",
    badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  EMBOSS_DIE: {
    prefix: "BN",
    name: "Bế nổi",
    techTerm: "Embossing - EMBOSSING",
    badgeClass: "bg-blue-50 text-blue-700 border-blue-200",
  },
  DEBOSS_DIE: {
    prefix: "BC",
    name: "Bế chìm",
    techTerm: "Debossing - DEBOSSING",
    badgeClass: "bg-indigo-50 text-indigo-700 border-indigo-200",
  },
  FOIL_STAMP: {
    prefix: "EN",
    name: "Ép nhũ / kim",
    techTerm: "Hot Foil Stamping - FOIL",
    badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
  },
  SPOT_UV: {
    prefix: "UV",
    name: "UV định hình",
    techTerm: "UV SPOT - SILKSCREEN",
    badgeClass: "bg-purple-50 text-purple-700 border-purple-200",
  },
  SILK_SCREEN: {
    prefix: "KL",
    name: "Kéo lụa / In lụa",
    techTerm: "Screen Printing - SILKSCREEN",
    badgeClass: "bg-cyan-50 text-cyan-700 border-cyan-200",
  },
  PLATE: {
    prefix: "CTP",
    name: "Bản kẽm in offset",
    techTerm: "CTP Plate - PLATE",
    badgeClass: "bg-slate-100 text-slate-700 border-slate-200",
  },
  GLUING_FIXTURE: {
    prefix: "KD",
    name: "Khuôn / Dưỡng dán hộp",
    techTerm: "Gluing Fixture - FOLDING",
    badgeClass: "bg-orange-50 text-orange-700 border-orange-200",
  },
};

export function POToolingTab({
  orderId,
  toolings = [],
  processes = [],
  dieCuts = [],
  plates = [],
  isPlanLocked = false,
  productionOrder,
}: {
  orderId: string;
  toolings?: any[];
  processes?: any[];
  dieCuts?: any[];
  plates?: any[];
  isPlanLocked?: boolean;
  productionOrder?: any;
}) {
  const queryClient = useQueryClient();

  // State controls
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const navigate = useNavigate();
  const [selectedToolingId, setSelectedToolingId] = useState<string | null>(null);

  // Mold Dispatch & Return State
  const [isDispatchMachineOpen, setIsDispatchMachineOpen] = useState(false);
  const [dispatchMachineName, setDispatchMachineName] = useState("Máy bế Bobst 102");
  const [dispatchOperator, setDispatchOperator] = useState("Thợ máy bế");

  const [isReturnWarehouseOpen, setIsReturnWarehouseOpen] = useState(false);
  const [returnLocation, setReturnLocation] = useState("KHO-KHUON/K01-B02");
  const [returnImpressions, setReturnImpressions] = useState<number>(0);
  const [returnCondition, setReturnCondition] = useState<"GOOD" | "NEED_MAINTENANCE" | "DAMAGED">("GOOD");
  const [returnNotes, setReturnNotes] = useState("Hoàn trả kho sau khi chạy xong đơn hàng");

  // Mold QR Spine Label Print State
  const [isPrintMoldLabelOpen, setIsPrintMoldLabelOpen] = useState(false);
  const [selectedMoldForLabel, setSelectedMoldForLabel] = useState<any>(null);

  // Modal: Record Mold Usage (Ghi nhận số lượt dập nóng)
  const [isRecordUsageOpen, setIsRecordUsageOpen] = useState(false);
  const [activeMoldForUsage, setActiveMoldForUsage] = useState<any>(null);
  const [usageImpressions, setUsageImpressions] = useState<number>(0);
  const [usageMachine, setUsageMachine] = useState("");
  const [usageOperator, setUsageOperator] = useState("");
  const [usageNotes, setUsageNotes] = useState("");

  // Modal: Order Tooling (Đặt khuôn ngoài)
  const [isOrderOpen, setIsOrderOpen] = useState(false);
  const [activeTooling, setActiveTooling] = useState<any>(null);
  const [orderVendorId, setOrderVendorId] = useState("");
  const [orderExpectedDate, setOrderExpectedDate] = useState("");
  const [orderUnitPrice, setOrderUnitPrice] = useState<number>(0);
  const [orderNotes, setOrderNotes] = useState("");

  // Modal: Receive Tooling (Nhập kho nghiệm thu)
  const [isReceiveOpen, setIsReceiveOpen] = useState(false);
  const [receiveLocation, setReceiveLocation] = useState("KHO-KHUON/K01");
  const [receiveNotes, setReceiveNotes] = useState("");

  // Modal: Verify Existing Tooling (Kiểm định khuôn cũ trước khi chạy)
  const [isVerifyOpen, setIsVerifyOpen] = useState(false);
  const [verifyNotes, setVerifyNotes] = useState("Kiểm tra dao bế sắc bén, đệm cao su đàn hồi tốt, boong khuôn chuẩn khớp bài in");

  // Modal: Assign Existing Mold (Gán khuôn có sẵn)
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [selectedMoldId, setSelectedMoldId] = useState("");

  // Modal: Add/Edit Tooling line manually
  const [isManualAddOpen, setIsManualAddOpen] = useState(false);
  const [isPrintCtpOpen, setIsPrintCtpOpen] = useState(false);
  const [manualType, setManualType] = useState("DIE_CUT");
  const [manualCode, setManualCode] = useState("");
  const [manualDim, setManualDim] = useState("");
  const [manualNotes, setManualNotes] = useState("");
  const [manualQty, setManualQty] = useState(1);
  const [manualPlateColor, setManualPlateColor] = useState("");
  const [manualDate, setManualDate] = useState("");

  // Lookups
  const { options: vendorOptions } = useVendorLookup(isOrderOpen);
  // danh sách NCC chỉ tải khi mở hộp đặt khuôn: tải xong thì chọn sẵn NCC đầu (ô chọn đang hiện NCC đó)
  useEffect(() => {
    if (isOrderOpen && !orderVendorId && vendorOptions.length > 0) setOrderVendorId(vendorOptions[0].id);
  }, [isOrderOpen, orderVendorId, vendorOptions]);
  const { options: moldOptions } = useMoldLookup(undefined, undefined, isAssignOpen);
  // Chỉ khuôn gắn được: không hỏng / bảo dưỡng / thanh lý / chờ nghiệm thu, đúng loại của dòng (cùng quy tắc máy chủ)
  const assignableMolds = moldOptions.filter((m) => isDieAssignableToLine(m.original, activeTooling?.toolingType));

  // Dòng khuôn/kẽm của lệnh; liên kết khuôn/kẽm cũ chỉ bổ sung khi lệnh chưa có dòng loại đó (không bịa giá trị)
  const isPlateType = (t: any) => t.toolingType === "PLATE" || t.toolingType === "ZINC";
  const moldToolings = (toolings || []).filter((t: any) => !isPlateType(t));
  const plateToolings = (toolings || []).filter(isPlateType);
  const displayMolds = moldToolings.length > 0
    ? moldToolings
    : (dieCuts || []).map((d: any) => ({
        id: d.id,
        moldCode: d.dieCutId || d.code || null,
        dieCutId: d.id,
        dieCut: d,
        toolingType: "DIE_CUT",
        dimensions: d.dimensions || null,
        status: "REUSE",
        readinessStatus: d.status === "AVAILABLE" ? "READY" : "PENDING",
        location: d.location || null,
        quantity: 1,
        isLegacy: true,
      }));
  const displayPlates = plateToolings.length > 0
    ? plateToolings
    : (plates || []).map((p: any) => ({
        id: p.id,
        moldCode: p.code || null,
        toolingType: "PLATE",
        plateSize: p.plateSize || null,
        plateColor: p.color || null,
        readinessStatus: p.status === "AVAILABLE" ? "READY" : "PENDING",
        quantity: p.setCount || 1,
        isLegacy: true,
      }));

  // Trạng thái kẽm CTP: lưu trong readinessStatus + thẻ [STATUS: …] ở ghi chú (dispatch/completeCtpPlates)
  const plateStage = (t: any): "PENDING_CTP" | "BURNING" | "CTP_READY" | "MOUNTED" => {
    const tag = /\[STATUS:\s*([A-Z_]+)\]/.exec(t.notes || "")?.[1];
    if (tag === "MOUNTED") return "MOUNTED";
    if (t.readinessStatus === "READY" || tag === "CTP_READY") return "CTP_READY";
    if (tag === "BURNING" || t.readinessStatus === "PARTIAL") return "BURNING";
    return "PENDING_CTP";
  };
  const PLATE_STAGE_LABEL = {
    PENDING_CTP: { label: "Chờ ghi bản", className: "bg-amber-50 text-amber-700 border-amber-300" },
    BURNING: { label: "Đang ghi bản", className: "bg-blue-50 text-blue-700 border-blue-300" },
    CTP_READY: { label: "Đã ghi bản xong", className: "bg-emerald-600 text-white border-emerald-600" },
    MOUNTED: { label: "Đã lên máy", className: "bg-slate-700 text-white border-slate-700" },
  } as const;

  // Cây Sản phẩm → Hợp phần của lệnh
  const lsxComponents = productionOrder?.components || [];
  const treeMode = isComponentTreeMode(lsxComponents);
  const componentNodes = orderComponentTree(
    lsxComponents,
    (productionOrder?.productionOrderSources || []).map((s: any) => s.id)
  );
  const nodeById = new Map(componentNodes.map((n) => [n.id, n]));

  const moldCount = displayMolds.reduce((n: number, t: any) => n + Math.max(1, Number(t.quantity) || 1), 0);
  const plateCount = displayPlates.reduce((n: number, t: any) => n + Math.max(1, Number(t.quantity) || 1), 0);
  const readyRowCount =
    displayMolds.filter((t: any) => t.readinessStatus === "READY").length +
    displayPlates.filter((t: any) => plateStage(t) === "CTP_READY" || plateStage(t) === "MOUNTED").length;
  const totalRowCount = displayMolds.length + displayPlates.length;
  // Gửi ghi bản chỉ cho kẽm nội bộ; kẽm thuê ngoài do bên in gia công ra kẽm
  const hasPendingPlates = plateToolings.some((t: any) => plateStage(t) === "PENDING_CTP" && !isOutsourcedPlate(t));
  const hasUnfinishedPlates = plateToolings.some((t: any) => plateStage(t) === "PENDING_CTP" || plateStage(t) === "BURNING");

  // Độ bền kẽm: số tờ chạy so với tuổi thọ 1 bộ kẽm (mực UV bền kém hơn)
  const runSheetsNeeded = Number(productionOrder?.runSheetsNeeded || productionOrder?.qtyPlan || 0);
  const isUvInk = Boolean(
    productionOrder?.techSpecs?.plateDurability?.isUvInk ||
      (productionOrder?.routingSteps || productionOrder?.routing || []).some((s: any) =>
        String(s.name || s.processName || "").toUpperCase().includes("UV")
      )
  );
  const plateDurability = evaluatePlateDurability({ runSheets: runSheetsNeeded, numColors: plateCount, isUvInk });

  // Phiếu ghi bản kẽm CTP: chỉ dữ liệu thật của các dòng kẽm
  const ctpPrintData = {
    plateId: `CTP-${productionOrder?.orderId || orderId}`,
    productionOrderId: productionOrder?.orderId || orderId,
    productName: productionOrder?.productName || productionOrder?.product?.name || "",
    plateSize: Array.from(new Set(displayPlates.map((p: any) => p.plateSize).filter(Boolean))).join(", "),
    colorCount: plateCount,
    statusLabel: hasUnfinishedPlates ? "Chờ ghi bản" : "Đã ghi bản xong",
    colors: displayPlates.map((p: any) => ({
      name: [p.plateColor || p.moldCode || p.toolingId, Number(p.quantity) > 1 ? `× ${p.quantity}` : ""].filter(Boolean).join(" "),
      note: String(p.notes || "").replace(/\s*\[STATUS:[^\]]+\]/g, "").trim(),
    })),
    createdAt: new Date(),
  };

  // Gửi ghi bản kẽm / Ghi bản xong cho toàn bộ dòng kẽm của lệnh
  const handlePlateBatch = async (action: "dispatch" | "complete") => {
    try {
      setIsSubmitting(true);
      if (action === "dispatch") {
        await trpcClient.productionOrders.dispatchCtpPlates.mutate({ id: orderId });
        toast.success("Đã gửi ghi bản kẽm cho tổ chế bản");
      } else {
        const res: any = await trpcClient.productionOrders.completeCtpPlates.mutate({ id: orderId });
        // Kho kẽm không đủ lá theo khổ: vẫn ghi nhận xuất, nhắc nhập kẽm ngay trong thông báo
        toast.success("Đã ghi nhận ghi bản kẽm xong", res?.stockWarnings?.length ? { description: res.stockWarnings.join(" ") } : undefined);
      }
      queryClient.invalidateQueries({ queryKey: ["production-order"] });
      queryClient.invalidateQueries({ queryKey: ["production-orders"] });
    } catch (err: any) {
      toast.error(err.message || "Lỗi cập nhật kẽm");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dispatch Mold/Tooling to Machine
  const handleDispatchMachineSubmit = async () => {
    if (!activeTooling) return;
    try {
      setIsSubmitting(true);
      await trpcClient.productionOrders.dispatchToolingToMachine.mutate({
        id: orderId,
        toolingId: activeTooling.id,
        machineName: dispatchMachineName,
        notes: dispatchOperator ? `Bàn giao cho: ${dispatchOperator}` : undefined,
      });
      toast.success(`Đã xuất bàn giao khuôn tới ${dispatchMachineName} thành công!`);
      setIsDispatchMachineOpen(false);
      queryClient.invalidateQueries({ queryKey: ["production-order"] });
      queryClient.invalidateQueries({ queryKey: ["molds"] });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi xuất khuôn bàn giao máy");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Return Mold/Tooling to Warehouse Rack
  const handleReturnWarehouseSubmit = async () => {
    if (!activeTooling) return;
    try {
      setIsSubmitting(true);
      await trpcClient.productionOrders.returnToolingToWarehouse.mutate({
        id: orderId,
        toolingId: activeTooling.id,
        returnLocation: returnLocation,
        actualImpressions: Math.round(returnImpressions),
        moldCondition: returnCondition,
        notes: returnNotes,
      });
      toast.success(`Đã hoàn trả khuôn về kệ ${returnLocation} & cập nhật số lượt dập!`);
      setIsReturnWarehouseOpen(false);
      queryClient.invalidateQueries({ queryKey: ["production-order"] });
      queryClient.invalidateQueries({ queryKey: ["molds"] });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi hoàn trả khuôn về kho");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Sync Action: Automatic Tooling Scanning & Synchronization
  const handleSyncToolings = async () => {
    try {
      setIsSubmitting(true);
      await trpcClient.productionOrders.syncToolings.mutate({ id: orderId });
      toast.success("Đã tự động quét quy trình & đồng bộ danh mục khuôn độc bản thành công!");
      queryClient.invalidateQueries({ queryKey: ["production-order"] });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi đồng bộ danh mục khuôn");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Order Modal
  const openOrderDialog = (tooling: any) => {
    setActiveTooling(tooling);
    setOrderVendorId(vendorOptions[0]?.id || "");
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 2);
    setOrderExpectedDate(toVnDateString(tomorrow));
    setOrderUnitPrice(250000);
    setOrderNotes(`Đặt làm mới cho LSX theo mã ${tooling.moldCode || tooling.processName}`);
    setIsOrderOpen(true);
  };

  // Submit Order Tooling Requisition
  const handleSubmitOrder = async () => {
    if (!orderVendorId) {
      toast.error("Vui lòng chọn Nhà cung cấp làm khuôn");
      return;
    }
    if (!orderExpectedDate) {
      toast.error("Vui lòng chọn ngày hẹn giao khuôn");
      return;
    }
    try {
      setIsSubmitting(true);
      await trpcClient.productionOrders.orderTooling.mutate({
        id: orderId,
        toolingId: activeTooling.id,
        vendorId: orderVendorId,
        expectedDate: new Date(orderExpectedDate),
        unitPrice: orderUnitPrice,
        notes: orderNotes,
      });
      toast.success(`Đã tạo đơn đặt gia công khuôn ngoài liên kết NCC thành công!`);
      setIsOrderOpen(false);
      queryClient.invalidateQueries({ queryKey: ["production-order"] });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi tạo đơn đặt khuôn");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Receive Modal
  const openReceiveDialog = (tooling: any) => {
    setActiveTooling(tooling);
    setReceiveLocation(tooling.location || "KHO-KHUON/K01-B02");
    setReceiveNotes("KCS kiểm tra đạt quy cách, nhập kho sẵn sàng chạy");
    setIsReceiveOpen(true);
  };

  // Submit Receive & Mark READY (Khuôn mới hoặc gia công ngoài)
  const handleSubmitReceive = async () => {
    if (!receiveLocation.trim()) {
      toast.error("Vui lòng nhập vị trí kệ kho lưu khuôn");
      return;
    }
    try {
      setIsSubmitting(true);
      await trpcClient.productionOrders.acceptAndStoreTooling.mutate({
        toolingId: activeTooling.id,
        location: receiveLocation.trim(),
        notes: receiveNotes,
      });
      toast.success(`Nghiệm thu khuôn vào vị trí ${receiveLocation.trim()} và chuyển trạng thái SẴN SÀNG!`);
      setIsReceiveOpen(false);
      queryClient.invalidateQueries({ queryKey: ["production-order"] });
      queryClient.invalidateQueries({ queryKey: ["molds"] });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi nghiệm thu nhập kho");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Verify Modal (Khuôn cũ tái sử dụng)
  const openVerifyDialog = (tooling: any) => {
    setActiveTooling(tooling);
    setVerifyNotes("Kiểm tra dao bế sắc bén, đệm cao su đàn hồi tốt, boong khuôn chuẩn khớp bài in");
    setIsVerifyOpen(true);
  };

  // Submit Verify Existing Mold
  const handleSubmitVerify = async () => {
    if (!activeTooling) return;
    try {
      setIsSubmitting(true);
      await trpcClient.productionOrders.verifyExistingTooling.mutate({
        toolingId: activeTooling.id,
        inspectionNotes: verifyNotes,
      });
      toast.success("Đã kiểm định khuôn đạt chuẩn kỹ thuật & chuyển trạng thái SẴN SÀNG!");
      setIsVerifyOpen(false);
      queryClient.invalidateQueries({ queryKey: ["production-order"] });
      queryClient.invalidateQueries({ queryKey: ["molds"] });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi kiểm định khuôn");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Assign Modal
  const openAssignDialog = (tooling: any) => {
    setActiveTooling(tooling);
    setSelectedMoldId("");
    setIsAssignOpen(true);
  };
  // Danh sách khuôn tải sau khi mở hộp: chọn sẵn khuôn gắn được đầu tiên
  useEffect(() => {
    if (isAssignOpen && !assignableMolds.some((m) => m.id === selectedMoldId)) setSelectedMoldId(assignableMolds[0]?.id || "");
  }, [isAssignOpen, assignableMolds, selectedMoldId]);

  // Submit Assign Existing Mold
  const handleSubmitAssign = async () => {
    if (!selectedMoldId) {
      toast.error("Vui lòng chọn khuôn có sẵn trong kho");
      return;
    }
    try {
      setIsSubmitting(true);
      await trpcClient.productionOrders.assignExistingMold.mutate({
        id: orderId,
        toolingId: activeTooling.id,
        dieCutId: selectedMoldId,
      });
      toast.success("Đã gán khuôn cũ từ kho thành công!");
      setIsAssignOpen(false);
      queryClient.invalidateQueries({ queryKey: ["production-order"] });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi gán khuôn cũ");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete Tooling line
  const handleDeleteTooling = async () => {
    if (!selectedToolingId) return;
    try {
      setIsSubmitting(true);
      await trpcClient.productionOrders.removeTooling.mutate({
        id: orderId,
        toolingId: selectedToolingId,
      });
      toast.success("Đã xóa dòng khuôn khỏi kế hoạch");
      setIsDeleteOpen(false);
      queryClient.invalidateQueries({ queryKey: ["production-order"] });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi xóa dòng khuôn");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Manual Add Tooling Line
  const handleManualAdd = async () => {
    try {
      setIsSubmitting(true);
      await trpcClient.productionOrders.addTooling.mutate({
        id: orderId,
        data: {
          toolingType: manualType,
          toolingId: manualCode.trim() || `${manualType}-${Date.now().toString(36).toUpperCase()}`,
          moldCode: manualCode.trim() || null,
          notes: manualNotes.trim() || null,
          quantity: Math.max(1, Number(manualQty) || 1),
          status: "NEW",
          ...(manualType === "PLATE"
            ? { plateSize: manualDim.trim() || null, plateColor: manualPlateColor.trim() || null }
            : { dimensions: manualDim.trim() || null }),
          expectedDate: manualDate || null,
        },
      });
      toast.success(manualType === "PLATE" ? "Đã thêm dòng kẽm" : "Đã thêm dòng khuôn");
      setIsManualAddOpen(false);
      setManualCode("");
      setManualDim("");
      setManualNotes("");
      setManualQty(1);
      setManualPlateColor("");
      setManualDate("");
      queryClient.invalidateQueries({ queryKey: ["production-order"] });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi thêm khuôn");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Record Usage Dialog
  const openRecordUsageDialog = (tool: any) => {
    setActiveMoldForUsage(tool);
    setUsageImpressions(0);
    setUsageMachine(tool.dieCut?.machine || "");
    setUsageOperator("");
    setUsageNotes(`Lượt dập theo Lệnh ${orderId}`);
    setIsRecordUsageOpen(true);
  };

  // Submit Mold Usage Record
  const handleRecordUsageSubmit = async () => {
    if (!activeMoldForUsage) return;
    const moldTargetId = activeMoldForUsage.dieCut?.id || activeMoldForUsage.dieCutId || activeMoldForUsage.id;
    if (!moldTargetId) {
      toast.error("Không tìm thấy mã khuôn trong CSDL");
      return;
    }
    if (usageImpressions <= 0) {
      toast.error("Vui lòng nhập số lượt dập lớn hơn 0");
      return;
    }
    try {
      setIsSubmitting(true);
      await trpcClient.technical.molds.recordUsage.mutate({
        dieCutId: moldTargetId,
        impressions: Math.round(usageImpressions),
        productionOrderId: orderId,
        poCode: orderId,
        machineName: usageMachine || undefined,
        operatorName: usageOperator || undefined,
        notes: usageNotes || undefined,
      });
      toast.success(`Đã ghi nhận ${formatNumber(usageImpressions)} lượt dập cho khuôn!`);
      setIsRecordUsageOpen(false);
      queryClient.invalidateQueries({ queryKey: ["production-order"] });
      queryClient.invalidateQueries({ queryKey: ["molds"] });
    } catch (err: any) {
      toast.error(err.message || "Lỗi ghi nhận lượt dập");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderMoldRow = (tool: any, index: number) => {
                    const standard = TOOLING_STANDARDS[tool.toolingType] || {
                      prefix: "KB",
                      name: "Khuôn bế",
                      techTerm: "Die Cutting",
                      badgeClass: "bg-slate-100 text-slate-700 border-slate-200",
                    };

                  const isReady = tool.readinessStatus === "READY";
                  const isReuse = tool.status === "REUSE";
                  const hasOrder = Boolean(tool.outsourcePoId);
                  const firstImage = Array.isArray(tool.dieCut?.images) && tool.dieCut.images.length > 0
                    ? tool.dieCut.images[0]
                    : tool.dieCut?.image;
                  // Chỉ liên kết sang trang khuôn khi khuôn đã có trong kho (dòng chỉ là mã dự kiến thì không có trang để mở)
                  const stockMoldKey = tool.dieCut?.dieCutId || tool.dieCut?.id || null;

                  return (
                    <TableRow key={tool.id || index} data-testid="po-tooling-row" data-kind="mold" className="hover:bg-slate-50/60 transition-colors border-b border-slate-100">
                      <TableCell className="text-center font-mono text-xs text-slate-400">
                        {index + 1}
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2">
                          {firstImage ? (
                            <ImageLightbox src={getFullUrl(firstImage)} alt={tool.moldCode}>
                              <img
                                src={getFullUrl(firstImage)}
                                alt={tool.moldCode}
                                className="w-8 h-8 rounded border border-slate-200 object-cover shrink-0 cursor-pointer hover:ring-2 hover:ring-indigo-400 transition-all"
                                title="Click xem ảnh phóng to"
                              />
                            </ImageLightbox>
                          ) : (
                            <div className="w-8 h-8 rounded bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 text-slate-400">
                              <ImageIcon className="w-4 h-4" />
                            </div>
                          )}
                          <div>
                            {(() => {
                              const isPlate = tool.toolingType === "PLATE";
                              const moldTargetKey = isPlate ? tool.dieCut?.dieCutId || tool.dieCutId || tool.moldCode : stockMoldKey;
                              return moldTargetKey ? (
                                <EntityLink
                                  entityType={isPlate ? "plate" : "mold"}
                                  id={moldTargetKey}
                                  code={tool.moldCode || tool.dieCut?.dieCutId || moldTargetKey}
                                  search={{ returnUrl: `/production-orders/${orderId}?tab=toolings` }}
                                  className="font-mono font-bold text-xs"
                                />
                              ) : (
                                <span
                                  className="inline-block font-mono font-bold text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-100"
                                  title="Khuôn chưa có trong kho"
                                  data-testid="po-tooling-code-pending"
                                >
                                  {tool.moldCode || "CHƯA_SINH_MÃ"}
                                </span>
                              );
                            })()}
                            {tool.dieCut?.version && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 ml-1 font-mono bg-slate-50 text-slate-600">
                                v{tool.dieCut.version}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="font-semibold text-slate-800 text-sm">
                          {tool.processName || standard.name}
                        </div>
                        <div className="text-xs text-slate-400 font-mono">
                          {tool.processCode || "PROC-FINISH"}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className={`font-mono font-bold ${standard.badgeClass}`}>
                            {standard.prefix}
                          </Badge>
                          <span className="text-xs text-slate-700 font-medium">
                            {standard.name}
                          </span>
                        </div>
                        <div className="text-[10px] text-slate-400 italic">
                          {standard.techTerm}
                        </div>
                      </TableCell>

                      <TableCell data-testid="po-tooling-spec">
                        <span className="text-xs font-mono text-slate-600">
                          {tool.dimensions || "Tiêu chuẩn"}
                        </span>
                        {/* Thông số khai báo trên dòng khuôn/kẽm (thay khối thông số gia công cũ) */}
                        {(tool.dieCutStyle || Number(tool.quantity) > 1 || tool.areaCm2 || tool.plateColor || tool.plateSize) && (
                          <div className="mt-0.5 text-[11px] text-slate-600 space-x-2">
                            {tool.dieCutStyle && <span>{String(tool.dieCutStyle).replace(/^Khác:\s*/, "")}</span>}
                            {tool.areaCm2 && <span>DT ép kim {Number(tool.areaCm2)} cm²</span>}
                            {tool.plateColor && <span>Màu {tool.plateColor}</span>}
                            {tool.plateSize && <span>Khổ {tool.plateSize}</span>}
                          </div>
                        )}
                      </TableCell>

                      <TableCell className="text-right font-mono text-xs font-bold text-slate-800" data-testid="po-tooling-qty">
                        {formatNumber(Math.max(1, Number(tool.quantity) || 1))}
                      </TableCell>

                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            isReuse
                              ? "bg-blue-50 text-blue-700 border-blue-200 text-xs"
                              : "bg-amber-50 text-amber-700 border-amber-200 text-xs"
                          }
                        >
                          {isReuse ? "Tái sử dụng" : "Khuôn mới"}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        {tool.location || tool.dieCut?.location ? (
                          <div className="flex items-center gap-1 text-xs font-semibold text-slate-700">
                            <MapPin className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                            <span>{formatLocation(tool.location || tool.dieCut?.location)}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">Chưa vào kệ</span>
                        )}
                      </TableCell>

                      <TableCell className="text-right font-mono">
                        <div className="font-bold text-slate-800 text-xs">
                          {formatNumber(tool.dieCut?.usageCount || 0)}
                        </div>
                        <div className="text-[9px] text-slate-400">lượt dập</div>
                      </TableCell>

                      {/* Cột Hạn Hoàn Thành (Deadline) */}
                      <TableCell className="text-center font-mono">
                        {tool.expectedDate ? (
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="font-semibold text-xs text-slate-800">
                              {format(new Date(tool.expectedDate), "dd/MM/yyyy HH:mm")}
                            </span>
                            {/* Cảnh báo làm gấp hoặc quá hạn */}
                            {(() => {
                              const expTime = new Date(tool.expectedDate).getTime();
                              const nowTime = new Date().getTime();
                              const diffHours = (expTime - nowTime) / (1000 * 60 * 60);

                              if (!isReady && expTime < nowTime) {
                                return (
                                  <Badge className="bg-rose-100 text-rose-800 border-rose-300 text-[9px] py-0 h-4">
                                    Quá Hạn Khuôn
                                  </Badge>
                                );
                              }
                              if (!isReady && !isReuse && diffHours > 0 && diffHours < 24) {
                                return (
                                  <Badge className="bg-amber-100 text-amber-900 border-amber-300 text-[9px] py-0 h-4 animate-pulse">
                                    ⚡ Làm Gấp (&lt;24h)
                                  </Badge>
                                );
                              }
                              return (
                                <span className="text-[10px] text-slate-400">
                                  {isReuse ? "Chuẩn bị sàn máy" : "Hạn nghiệm thu"}
                                </span>
                              );
                            })()}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">Trước giờ bế 2h</span>
                        )}
                      </TableCell>

                      <TableCell className="text-center">
                        {isReady ? (
                          <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1 text-xs py-0.5">
                            <CheckCircle2 className="h-3 w-3" /> Sẵn Sàng Tại Kệ
                          </Badge>
                        ) : hasOrder ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 text-xs py-0.5">
                            Đang Gia Công Ngoài
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-xs py-0.5">
                            Chờ Nghiệm Thu
                          </Badge>
                        )}
                      </TableCell>

                      <TableCell className="text-right pr-4">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Nút xem chi tiết khuôn trong kho */}
                          {Boolean(stockMoldKey) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                              onClick={() =>
                                navigate({
                                  to: "/molds/$moldId",
                                  params: { moldId: stockMoldKey },
                                })
                              }
                              title="Xem chi tiết khuôn trong kho"
                              data-testid="po-tooling-view-mold"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          )}

                          {/* Nút ghi nhận lượt dập nóng */}
                          {Boolean(stockMoldKey) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 px-2 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                              onClick={() => openRecordUsageDialog(tool)}
                              disabled={isPlanLocked}
                              title="Ghi nhận số lượt dập cho khuôn này"
                            >
                              <Gauge className="h-3.5 w-3.5 mr-1 text-indigo-600" />
                              Dập
                            </Button>
                          )}

                          {/* Nút In Tem QR Gáy Kệ */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 px-2 text-xs border-slate-200 text-slate-700 hover:bg-slate-100"
                            onClick={() => {
                              setSelectedMoldForLabel({
                                ...(tool.dieCut || {}),
                                ...tool,
                                // QR và mã trên tem theo khuôn trong kho (không lấy id dòng khuôn của lệnh);
                                // khuôn dự kiến dùng mã dự kiến — khi nghiệm thu khuôn được tạo đúng mã này
                                id: tool.dieCut?.id,
                                dieCutId: tool.dieCut?.dieCutId || tool.moldCode,
                                moldCode: tool.moldCode || tool.dieCut?.dieCutId || "KB-01",
                                name: tool.processName || tool.dieCut?.name || "Khuôn bế bao bì",
                                productName: productionOrder?.productName || "Bao bì sản phẩm",
                                customerName: productionOrder?.customer?.name || "Artpresto",
                                location: tool.location || tool.dieCut?.location || "KHO-KHUON/K01",
                              });
                              setIsPrintMoldLabelOpen(true);
                            }}
                            title="In Tem Gáy Kệ QR Tra Cứu"
                          >
                            <QrCode className="h-3.5 w-3.5 mr-1 text-indigo-600" />
                            Tem QR
                          </Button>

                          {/* Nút Xuất Bàn Giao Máy */}
                          {isReady && !tool.location?.startsWith("MÁY") && tool.status !== "IN_USE" && (
                            <Button
                              size="sm"
                              className="h-8 px-2 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                              onClick={() => {
                                setActiveTooling(tool);
                                setDispatchMachineName(tool.dieCut?.machine || (tool.processName?.includes("bế") ? "Máy bế Bobst 102" : "Máy dập ép nhũ"));
                                setIsDispatchMachineOpen(true);
                              }}
                              disabled={isPlanLocked}
                              title="Xuất kho bàn giao khuôn cho máy sản xuất"
                            >
                              <Truck className="h-3.5 w-3.5 mr-1" />
                              Xuất Máy
                            </Button>
                          )}

                          {/* Nút Thu Hồi Về Kho */}
                          {(tool.location?.startsWith("MÁY") || tool.status === "IN_USE" || tool.dieCut?.status === "IN_USE") && (
                            <Button
                              size="sm"
                              className="h-8 px-2 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                              onClick={() => {
                                setActiveTooling(tool);
                                setReturnLocation(tool.dieCut?.location || "KHO-KHUON/K01-B02");
                                setReturnImpressions(tool.quantity || 1000);
                                setIsReturnWarehouseOpen(true);
                              }}
                              disabled={isPlanLocked}
                              title="Thu hồi khuôn về kệ kho và ghi nhận số lượt dập"
                            >
                              <Undo2 className="h-3.5 w-3.5 mr-1" />
                              Thu Hồi Kho
                            </Button>
                          )}

                          {!isReady && !hasOrder && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 px-2 text-xs border-amber-200 text-amber-800 hover:bg-amber-50"
                              onClick={() => openOrderDialog(tool)}
                              disabled={isPlanLocked}
                              title="Đặt làm khuôn mới ngoài xưởng"
                            >
                              <PackagePlus className="h-3.5 w-3.5 mr-1 text-amber-600" />
                              Đặt Mới
                            </Button>
                          )}

                          {!isReady && isReuse && (
                            <Button
                              size="sm"
                              className="h-8 px-2 text-xs bg-cyan-700 hover:bg-cyan-800 text-white font-semibold flex items-center gap-1 shadow-xs"
                              onClick={() => openVerifyDialog(tool)}
                              disabled={isPlanLocked}
                              title="Kiểm định kỹ thuật boong/dao khuôn cũ và chuyển trạng thái SẴN SÀNG"
                            >
                              <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                              Kiểm Định
                            </Button>
                          )}

                          {!isReady && !isReuse && (
                            <Button
                              size="sm"
                              className="h-8 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center gap-1 shadow-xs"
                              onClick={() => openReceiveDialog(tool)}
                              disabled={isPlanLocked}
                              title="Nghiệm thu nhập kho KHO-KHUON và mở khóa trạm máy"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                              Nghiệm Thu
                            </Button>
                          )}

                          {!isReady && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-slate-500 hover:text-slate-800"
                              onClick={() => openAssignDialog(tool)}
                              disabled={isPlanLocked}
                              title="Chọn khuôn cũ có sẵn từ kho"
                            >
                              <Search className="h-3.5 w-3.5" />
                            </Button>
                          )}

                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                            onClick={() => {
                              setSelectedToolingId(tool.id);
                              setIsDeleteOpen(true);
                            }}
                            disabled={isPlanLocked}
                            title="Xóa dòng khuôn này"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
};

  // Chế độ cây: nhóm dòng khuôn + kẽm theo hợp phần (cha trước con), dòng không gắn hợp phần là "Dùng chung"
  const renderToolingRows = () => {
    if (!treeMode) {
      return [
        ...displayMolds.map((tool: any, i: number) => renderMoldRow(tool, i)),
        ...displayPlates.map((plate: any, i: number) => renderPlateRow(plate, displayMolds.length + i)),
      ];
    }
    const rows: any[] = [];
    let n = 0;
    const groups = [...componentNodes.map((node) => node.id), null];
    for (const compId of groups) {
      const molds = displayMolds.filter((t: any) => (t.componentId || null) === compId || (compId === null && t.componentId && !nodeById.has(t.componentId)));
      const plates = displayPlates.filter((t: any) => (t.componentId || null) === compId || (compId === null && t.componentId && !nodeById.has(t.componentId)));
      if (molds.length + plates.length === 0) continue;
      rows.push(
        <ComponentGroupRow
          key={`grp-${compId || "chung"}`}
          node={compId ? nodeById.get(compId) || null : null}
          colSpan={12}
          count={molds.length + plates.length}
        />
      );
      molds.forEach((t: any) => rows.push(renderMoldRow(t, n++)));
      plates.forEach((t: any) => rows.push(renderPlateRow(t, n++)));
    }
    return rows;
  };

  const renderPlateRow = (plate: any, index: number) => {
    const stage = plateStage(plate);
    const stageInfo = PLATE_STAGE_LABEL[stage];
    const standard = TOOLING_STANDARDS.PLATE;
    return (
      <TableRow key={plate.id || `plate-${index}`} data-testid="po-tooling-row" data-kind="plate" className="hover:bg-slate-50/60 transition-colors border-b border-slate-100">
        <TableCell className="text-center font-mono text-xs text-slate-400">{index + 1}</TableCell>
        <TableCell>
          <span className="inline-block font-mono font-bold text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-100">
            {plate.moldCode || plate.toolingId || "Kẽm in"}
          </span>
        </TableCell>
        <TableCell>
          <div className="font-semibold text-slate-800 text-sm">{plate.processName || "In offset"}</div>
          {plate.processCode && <div className="text-xs text-slate-400 font-mono">{plate.processCode}</div>}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1.5">
            <Badge variant="outline" className={`font-mono font-bold ${standard?.badgeClass || ""}`}>CTP</Badge>
            <span className="text-xs text-slate-700 font-medium">Bản kẽm</span>
          </div>
        </TableCell>
        <TableCell data-testid="po-tooling-spec">
          <div className="text-[11px] text-slate-600 space-x-2">
            {plate.plateColor && <span>Màu {plate.plateColor}</span>}
            {plate.plateSize && <span>Khổ {plate.plateSize}</span>}
            {!plate.plateColor && !plate.plateSize && <span className="text-slate-400 italic">{plate.dimensions || "Chưa khai báo màu/khổ"}</span>}
          </div>
        </TableCell>
        <TableCell className="text-right font-mono text-xs font-bold text-slate-800" data-testid="po-tooling-qty">
          {formatNumber(Math.max(1, Number(plate.quantity) || 1))}
        </TableCell>
        <TableCell>
          {plate.plateSource ? (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
              {isOutsourcedPlate(plate) ? "Thuê ngoài (không trừ kho)" : plate.plateSource === "INTERNAL" ? "CTP nội bộ" : plate.plateSource}
            </Badge>
          ) : (
            <span className="text-xs text-slate-400">—</span>
          )}
        </TableCell>
        <TableCell><span className="text-xs text-slate-400">—</span></TableCell>
        <TableCell className="text-right"><span className="text-xs text-slate-400">—</span></TableCell>
        <TableCell className="text-center font-mono">
          {plate.expectedDate ? (
            <span className="font-semibold text-xs text-slate-800">{format(new Date(plate.expectedDate), "dd/MM/yyyy HH:mm")}</span>
          ) : (
            <span className="text-xs text-slate-400">—</span>
          )}
        </TableCell>
        <TableCell className="text-center">
          <Badge variant="outline" className={`text-xs py-0.5 ${stageInfo.className}`} data-testid="po-plate-stage">
            {stageInfo.label}
          </Badge>
        </TableCell>
        <TableCell className="text-right pr-4">
          {!plate.isLegacy && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
              onClick={() => {
                setSelectedToolingId(plate.id);
                setIsDeleteOpen(true);
              }}
              disabled={isPlanLocked}
              title="Xóa dòng kẽm này"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="space-y-6">
      {/* ========================================================================= */}
      {/* KHUÔN BẾ & ĐỒ GÁ TÁI SỬ DỤNG (5 LOẠI: KB, BN, BC, EN, UV)               */}
      {/* ========================================================================= */}
      <div className="space-y-4">
        {/* Tóm tắt + thao tác của riêng lệnh này */}
        <div
          data-testid="po-tooling-header"
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-slate-50/80 p-3 rounded-lg border border-slate-200"
        >
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <Wrench className="h-4 w-4 text-emerald-600" />
              Khuôn &amp; kẽm
            </h4>
            <Badge variant="outline" className="text-[10px] font-mono" data-testid="po-tooling-ready" title={`${moldCount} khuôn · ${plateCount} bản kẽm`}>
              {readyRowCount}/{totalRowCount} sẵn sàng
            </Badge>
            {(plateCount > 0 || runSheetsNeeded > 0) && (
              <Badge
                variant="outline"
                data-testid="po-ctp-plate-durability-badge"
                title={plateDurability.recommendation}
                className={`font-mono text-[11px] ${
                  plateDurability.setsRequired > 1 ? "bg-rose-50 text-rose-800 border-rose-300 font-bold" : "bg-indigo-50 text-indigo-800 border-indigo-200"
                }`}
              >
                <Flame className="h-3 w-3 mr-1 text-amber-600" />
                Độ bền kẽm: {runSheetsNeeded.toLocaleString("vi-VN")}/{plateDurability.maxImpressionsPerSet.toLocaleString("vi-VN")} lượt ({plateDurability.setsRequired} bộ)
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
            {displayPlates.length > 0 && (
              <Button size="sm" variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50 font-medium" onClick={() => setIsPrintCtpOpen(true)}>
                <Printer className="h-4 w-4 mr-1.5" />
                In phiếu kẽm
              </Button>
            )}
            {hasPendingPlates && (
              <Button
                size="sm"
                variant="outline"
                className="border-blue-200 text-blue-700 hover:bg-blue-50 font-medium"
                onClick={() => handlePlateBatch("dispatch")}
                disabled={isPlanLocked || isSubmitting}
              >
                <Send className="h-4 w-4 mr-1.5" />
                Gửi ghi bản kẽm
              </Button>
            )}
            {hasUnfinishedPlates && (
              <Button
                size="sm"
                variant="outline"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 font-medium"
                onClick={() => handlePlateBatch("complete")}
                disabled={isPlanLocked || isSubmitting}
              >
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
                Ghi bản kẽm xong
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 font-medium"
              onClick={handleSyncToolings}
              disabled={isPlanLocked || isSubmitting}
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${isSubmitting ? "animate-spin" : ""}`} />
              Quét &amp; đồng bộ
            </Button>
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
              onClick={() => setIsManualAddOpen(true)}
              disabled={isPlanLocked}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Thêm khuôn / kẽm
            </Button>
          </div>
        </div>

        {plateDurability.setsRequired > 1 && (
          <div data-testid="po-ctp-plate-durability-banner" className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 flex items-center gap-2 text-xs text-rose-900 font-medium">
            <Flame className="h-4 w-4 text-rose-600 shrink-0" />
            <span>{plateDurability.recommendation}</span>
          </div>
        )}

        {/* Main Table for Molds */}
        <Card className="border-slate-200 shadow-xs overflow-hidden">
          <CardContent className="p-0">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow className="border-b border-slate-200">
                  <TableHead className="w-12 text-center text-xs font-bold text-slate-600">#</TableHead>
                  <TableHead className="text-xs font-bold text-slate-600">Mã Khuôn</TableHead>
                  <TableHead className="text-xs font-bold text-slate-600">Công Đoạn</TableHead>
                  <TableHead className="text-xs font-bold text-slate-600">Loại khuôn</TableHead>
                  <TableHead className="text-xs font-bold text-slate-600">Quy cách</TableHead>
                  <TableHead className="text-xs font-bold text-slate-600 text-right">SL</TableHead>
                  <TableHead className="text-xs font-bold text-slate-600">Nguồn gốc</TableHead>
                  <TableHead className="text-xs font-bold text-slate-600">Vị trí kệ kho</TableHead>
                  <TableHead className="text-xs font-bold text-slate-600 text-right">Lượt dập</TableHead>
                  <TableHead className="text-xs font-bold text-slate-600 text-center w-40">Hạn hoàn thành</TableHead>
                  <TableHead className="text-xs font-bold text-slate-600 text-center">Trạng thái</TableHead>
                  <TableHead className="text-right text-xs font-bold text-slate-600 pr-4">Thao tác</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {totalRowCount > 0 ? (
                  renderToolingRows()
              ) : (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-12 text-slate-500">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Wrench className="h-8 w-8 text-slate-300" />
                      <p className="text-sm font-medium">Lệnh chưa có dòng khuôn hoặc kẽm</p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 border-indigo-200 text-indigo-700"
                        onClick={handleSyncToolings}
                      >
                        <RefreshCw className="h-4 w-4 mr-1.5" />
                        Quét Quy Trình &amp; Tự Động Tạo Danh Mục Khuôn
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </div>

      {/* Modal: Đặt khuôn mới (Order Tooling Dialog) */}
      <Dialog open={isOrderOpen} onOpenChange={setIsOrderOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackagePlus className="h-5 w-5 text-amber-600" />
              Đặt Làm Khuôn Ngoài (Tooling Requisition)
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Tạo đơn đặt gia công khuôn ngoài và gắn mã theo dõi tiến độ cho LSX.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div className="bg-slate-50 p-3 rounded border border-slate-200 space-y-1">
              <div className="text-xs font-medium text-slate-500">Công đoạn &amp; Mã khuôn:</div>
              <div className="font-bold text-sm text-slate-800">
                {activeTooling?.processName || activeTooling?.toolingType}
              </div>
              <div className="font-mono text-xs font-bold text-indigo-700">
                {activeTooling?.moldCode || "CHƯA_XÁC_ĐỊNH"}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Nhà Cung Cấp Gia Công Khuôn *</label>
              <select
                className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm shadow-xs focus:ring-1 focus:ring-indigo-500"
                value={orderVendorId}
                onChange={(e) => setOrderVendorId(e.target.value)}
              >
                {vendorOptions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.label} {v.meta ? `(${v.meta})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Ngày Hẹn Giao *</label>
                <input
                  type="date"
                  className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm shadow-xs"
                  value={orderExpectedDate}
                  onChange={(e) => setOrderExpectedDate(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Đơn Giá Dự Kiến (VNĐ)</label>
                <NumericInput
                  value={orderUnitPrice}
                  onChange={(e) => setOrderUnitPrice(Number(e.target.value) || 0)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Ghi Chú Kỹ Thuật Đặt Khuôn</label>
              <Input
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                placeholder="Yêu cầu dao bế Nhật, đệm mút cao su, đường gân..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOrderOpen(false)}>
              Đóng
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white font-medium"
              onClick={handleSubmitOrder}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Đang xử lý..." : "Xác Nhận Đặt Khuôn"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Nhập kho nghiệm thu (Receive Tooling Dialog) */}
      <Dialog open={isReceiveOpen} onOpenChange={setIsReceiveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Nghiệm Thu &amp; Nhập Kệ Kho Khuôn
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Xác nhận khuôn đã về xưởng, đạt chất lượng KCS và gán vị trí kệ kho sẵn sàng chạy.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div className="bg-emerald-50/60 p-3 rounded border border-emerald-200 space-y-1">
              <div className="text-xs font-medium text-emerald-700">Mã khuôn nghiệm thu:</div>
              <div className="font-mono font-bold text-sm text-emerald-900">
                {activeTooling?.moldCode}
              </div>
              <div className="text-xs text-emerald-700">
                Quy cách: {activeTooling?.dimensions || "Tiêu chuẩn"}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Vị Trí Kệ Kho (Aisle / Shelf / Bin) *</label>
              <Input
                value={receiveLocation}
                onChange={(e) => setReceiveLocation(e.target.value)}
                placeholder="Ví dụ: KHO-KHUON/K02-B04 hoặc KE-01-A"
              />
              <p className="text-[11px] text-slate-500">
                Ghi nhớ vị trí để thợ máy bế/nhũ dễ dàng lấy khuôn khi vào ca chạy.
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Ghi Chú Nghiệm Thu KCS</label>
              <Input
                value={receiveNotes}
                onChange={(e) => setReceiveNotes(e.target.value)}
                placeholder="Khuôn bén sắc, dao chuẩn 23.8mm, đã test mẫu..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReceiveOpen(false)}>
              Hủy
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
              onClick={handleSubmitReceive}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Đang lưu..." : "Xác Nhận Sẵn Sàng (READY)"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Gán khuôn có sẵn (Assign Mold Dialog) */}
      <Dialog open={isAssignOpen} onOpenChange={setIsAssignOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-5 w-5 text-indigo-600" />
              Gán Khuôn Có Sẵn Từ Kho
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Tái sử dụng khuôn cũ sẵn có trong kho phù hợp quy cách sản phẩm.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Chọn Khuôn Từ Thư Viện Kho *</label>
              <select
                className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm shadow-xs focus:ring-1 focus:ring-indigo-500"
                value={selectedMoldId}
                onChange={(e) => setSelectedMoldId(e.target.value)}
              >
                {assignableMolds.length === 0 && <option value="">Không có khuôn sẵn dùng đúng loại trong kho</option>}
                {assignableMolds.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label} {m.meta ? `(${m.meta})` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAssignOpen(false)}>
              Hủy
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
              onClick={handleSubmitAssign}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Đang gán..." : "Gán Khuôn Này"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Thêm thủ công dòng khuôn */}
      <Dialog open={isManualAddOpen} onOpenChange={setIsManualAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-indigo-600" />
              Thêm dòng khuôn / kẽm
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Dòng mới chỉ thuộc lệnh sản xuất này.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3.5 py-2">
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Loại *</label>
              <select
                className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm shadow-xs"
                value={manualType}
                onChange={(e) => setManualType(e.target.value)}
              >
                {Object.entries(TOOLING_STANDARDS).map(([key, info]) => (
                  <option key={key} value={key}>
                    [{info.prefix}] {info.name} ({info.techTerm})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Mã (tùy chọn)</label>
              <Input
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="Để trống để tự sinh theo chuẩn [Prefix]-[MãSP]-[KT]"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">{manualType === "PLATE" ? "Khổ kẽm" : "Kích thước quy cách"}</label>
              <Input
                value={manualDim}
                onChange={(e) => setManualDim(e.target.value)}
                placeholder={manualType === "PLATE" ? "Ví dụ: 1030x800 mm" : "Ví dụ: 200x200x400 mm"}
              />
            </div>

            {manualType === "PLATE" && (
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Màu</label>
                <Input
                  value={manualPlateColor}
                  onChange={(e) => setManualPlateColor(e.target.value)}
                  placeholder="Ví dụ: C, M, Y, K, Pantone 485"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Số lượng *</label>
                <NumericInput value={manualQty} onChange={(e) => setManualQty(Number(e.target.value) || 1)} min={1} data-testid="po-tooling-add-qty" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Ngày cần</label>
                <Input type="date" value={manualDate} onChange={(e) => setManualDate(e.target.value)} />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">Ghi Chú</label>
              <Input
                value={manualNotes}
                onChange={(e) => setManualNotes(e.target.value)}
                placeholder="Ghi chú thêm..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManualAddOpen(false)}>
              Hủy
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
              onClick={handleManualAdd}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Đang thêm..." : "Thêm Vào Kế Hoạch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        title="Xác nhận xóa dòng khuôn"
        description="Bạn có chắc chắn muốn xóa dòng khuôn này khỏi Lệnh Sản Xuất?"
        onConfirm={handleDeleteTooling}
        variant="destructive"
      />

      {/* Modal: Ghi nhận số lượt dập khuôn (Record Usage Dialog) */}
      <Dialog open={isRecordUsageOpen} onOpenChange={setIsRecordUsageOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gauge className="h-5 w-5 text-indigo-600" />
              Ghi Nhận Lượt Dập Khuôn
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Cập nhật số lượt dập thực tế của khuôn cho Lệnh Sản Xuất này để tích lũy hao mòn và bảo trì định kỳ.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div className="bg-slate-50 p-3 rounded border border-slate-200 space-y-1">
              <div className="text-xs font-medium text-slate-500">Khuôn &amp; Công đoạn:</div>
              <div className="font-bold text-sm text-slate-800">
                {activeMoldForUsage?.moldCode || activeMoldForUsage?.dieCut?.dieCutId || "KHUON-CHUA-RO"}
              </div>
              <div className="text-xs text-slate-600">
                Lượt dập hiện tại: <span className="font-mono font-bold text-indigo-700">{formatNumber(activeMoldForUsage?.dieCut?.usageCount || 0)}</span>
                {activeMoldForUsage?.dieCut?.maxImpressions ? ` / ${formatNumber(activeMoldForUsage.dieCut.maxImpressions)} tối đa` : ""}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Số Lượt Dập Bổ Sung (Impressions) *</label>
              <NumericInput
                value={usageImpressions}
                onChange={(e) => setUsageImpressions(Number(e.target.value) || 0)}
                placeholder="Ví dụ: 5000"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Máy Dập / Máy Chạy</label>
                <Input
                  value={usageMachine}
                  onChange={(e) => setUsageMachine(e.target.value)}
                  placeholder="Ví dụ: MAY-BE-01"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Thợ Vận Hành</label>
                <Input
                  value={usageOperator}
                  onChange={(e) => setUsageOperator(e.target.value)}
                  placeholder="Ví dụ: Nguyễn Văn A"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Ghi Chú Vận Hành</label>
              <Input
                value={usageNotes}
                onChange={(e) => setUsageNotes(e.target.value)}
                placeholder="Tình trạng dao, đệm mút, áp lực..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRecordUsageOpen(false)}>
              Đóng
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
              onClick={handleRecordUsageSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Đang lưu..." : "Xác Nhận Ghi Nhận"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL: Xuất Bàn Giao Khuôn Tới Máy */}
      <Dialog open={isDispatchMachineOpen} onOpenChange={setIsDispatchMachineOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-indigo-600" />
              Xuất Kho Bàn Giao Khuôn Tới Trạm Máy
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Ghi nhận xuất kho bàn giao khuôn từ kệ sang máy sản xuất sẵn sàng lên bài chạy.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="bg-slate-50 p-3 rounded border border-slate-200 text-xs space-y-1">
              <div>Mã khuôn: <strong className="text-indigo-700 font-mono font-bold">{activeTooling?.moldCode || activeTooling?.dieCut?.dieCutId}</strong></div>
              <div>Công đoạn: <strong className="text-slate-800">{activeTooling?.processName}</strong></div>
              <div>Vị trí kho hiện tại: <span className="text-slate-600 font-mono">{activeTooling?.location || "KHO-KHUON"}</span></div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Máy Sản Xuất Tiếp Nhận *</label>
              <Input
                value={dispatchMachineName}
                onChange={(e) => setDispatchMachineName(e.target.value)}
                placeholder="Ví dụ: Máy bế Bobst 102, Máy ép nhũ Heidelberg..."
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Người Tiếp Nhận / Thợ Máy</label>
              <Input
                value={dispatchOperator}
                onChange={(e) => setDispatchOperator(e.target.value)}
                placeholder="Ví dụ: Thợ máy bế - Trần Văn B"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDispatchMachineOpen(false)}>
              Hủy
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={handleDispatchMachineSubmit}
              disabled={isSubmitting || !dispatchMachineName.trim()}
            >
              {isSubmitting ? "Đang xuất kho..." : "Xác Nhận Bàn Giao Máy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL: Thu Hồi Khuôn Về Kho */}
      <Dialog open={isReturnWarehouseOpen} onOpenChange={setIsReturnWarehouseOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="h-5 w-5 text-amber-600" />
              Thu Hồi Khuôn Về Kệ Kho Sau Sản Xuất
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Kiểm tra tình trạng lưỡi dao/đế khuôn, ghi nhận số lượt dập trong ca và lưu vị trí kệ kho.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="bg-slate-50 p-3 rounded border border-slate-200 text-xs space-y-1">
              <div>Mã khuôn: <strong className="text-indigo-700 font-mono font-bold">{activeTooling?.moldCode || activeTooling?.dieCut?.dieCutId}</strong></div>
              <div>Lũy kế dập hiện tại: <span className="font-mono font-bold text-slate-700">{formatNumber(activeTooling?.dieCut?.usageCount || 0)}</span> lượt</div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Vị Trí Kệ Kho Cất Khuôn *</label>
              <Input
                value={returnLocation}
                onChange={(e) => setReturnLocation(e.target.value)}
                placeholder="Ví dụ: KHO-KHUON/K01-B02"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Số Lượt Dập Trong Ca Này (Impressions)</label>
              <NumericInput
                value={returnImpressions}
                onChange={(e) => setReturnImpressions(Number(e.target.value) || 0)}
                placeholder="Ví dụ: 3500"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Tình Trạng Kỹ Thuật Sau Khi Chạy</label>
              <select
                value={returnCondition}
                onChange={(e) => setReturnCondition(e.target.value as any)}
                className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-xs text-slate-800 shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="GOOD">Tốt - Dao bế sắc bén, đế phẳng (AVAILABLE)</option>
                <option value="NEED_MAINTENANCE">Cần Bảo Dưỡng - Cùn dao nhẹ / đệm mút lún (MAINTENANCE)</option>
                <option value="DAMAGED">Hư Hỏng Nặng - Mẻ lưỡi dao / nứt đế gỗ (DAMAGED)</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Ghi Chú Kiểm Tra Mặt Dao</label>
              <Input
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
                placeholder="Ví dụ: Vệ sinh sạch bụi giấy, bảo quản bao nilon..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReturnWarehouseOpen(false)}>
              Hủy
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleReturnWarehouseSubmit}
              disabled={isSubmitting || !returnLocation.trim()}
            >
              {isSubmitting ? "Đang lưu..." : "Xác Nhận Nhập Kho"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL: Kiểm Định Kỹ Thuật Khuôn Cũ (REUSE) */}
      <Dialog open={isVerifyOpen} onOpenChange={setIsVerifyOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-cyan-600" />
              Kiểm Định Kỹ Thuật Khuôn Cũ (Tái Sử Dụng)
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Kiểm tra tình trạng vật lý của khuôn có sẵn trong kho trước khi chuyển sang trạng thái SẴN SÀNG điều độ máy.
            </DialogDescription>
          </DialogHeader>

          {activeTooling && (
            <div className="space-y-3 py-2 text-xs">
              <div className="bg-slate-50 p-3 rounded border border-slate-200 space-y-1">
                <div>Mã khuôn: <strong className="font-mono font-bold text-indigo-700">{activeTooling.moldCode || activeTooling.dieCut?.dieCutId}</strong></div>
                <div>Công đoạn: <span className="font-semibold text-slate-800">{activeTooling.processName}</span></div>
                <div>Quy cách: <span className="font-mono text-slate-700">{activeTooling.dimensions || "Tiêu chuẩn"}</span></div>
                <div>Vị trí lưu kho: <span className="font-semibold text-emerald-700">{activeTooling.location || activeTooling.dieCut?.location || "KHO-KHUON"}</span></div>
                <div>Lũy kế dập: <span className="font-mono font-bold text-slate-800">{formatNumber(activeTooling.dieCut?.usageCount || 0)}</span> lượt</div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Tiêu chí kiểm định kỹ thuật:</label>
                <div className="space-y-1 text-slate-600 bg-cyan-50/50 p-2.5 rounded border border-cyan-100">
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-cyan-600 shrink-0" /> Lưỡi dao bế / gân cấn nguyên vẹn, không mẻ dao.
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-cyan-600 shrink-0" /> Đệm mút cao su tống phôi đàn hồi đạt chuẩn.
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-cyan-600 shrink-0" /> Khớp chính xác với ma-két / bài in mới.
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Ghi chú kết quả kiểm định:</label>
                <Input
                  value={verifyNotes}
                  onChange={(e) => setVerifyNotes(e.target.value)}
                  placeholder="Ghi chú đánh giá tình trạng..."
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsVerifyOpen(false)}>
              Hủy
            </Button>
            <Button
              className="bg-cyan-700 hover:bg-cyan-800 text-white font-semibold flex items-center gap-1"
              onClick={handleSubmitVerify}
              disabled={isSubmitting}
            >
              <ShieldCheck className="h-4 w-4" />
              {isSubmitting ? "Đang lưu..." : "Xác Nhận Đạt Chuẩn READY"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG IN: Tem Gáy Kệ Khuôn QR ISO 9001 */}
      <PrintDialog
        open={isPrintMoldLabelOpen}
        onClose={() => setIsPrintMoldLabelOpen(false)}
        title="Tem Gáy Kệ Khuôn Bế QR Code"
        documentTitle={`TEM_KHUON_${selectedMoldForLabel?.moldCode || "LABEL"}`}
      >
        <MoldLabelPrintTemplate data={selectedMoldForLabel} />
      </PrintDialog>
      <PrintDialog
        open={isPrintCtpOpen}
        onClose={() => setIsPrintCtpOpen(false)}
        type="ctp_plate"
        data={ctpPrintData}
        title="Phiếu ghi bản kẽm CTP"
        documentTitle={`CTP_${productionOrder?.orderId || orderId}`}
      />
    </div>
  );
}
